import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Flex,
  Img,
  Input,
  InputLeftAddon,
  InputGroup,
  Text,
  Textarea,
  Switch,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet";
import colors from "../../constants/colors";
import { MdEmail, MdFileUpload, MdPhoneIphone, MdStore } from "react-icons/md";
import { FiUser } from "react-icons/fi";
import { IoLink } from "react-icons/io5";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import slugify from "slugify";
import API from "../../api";

import SectionHeader from "../../components/SectionHeader";
import ColorPicker from "../../components/ColorPicker";
import FormError from "../../components/FormError";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import toast from "react-hot-toast";
import withNavElements from "../../components/withNavElements";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("El nombre es requerido"),
  description: Yup.string()
    .required("La descripción es requerida")
    .max(200, "La descripción no puede tener más de 200 caracteres"),
  logo: Yup.string(),
  banner: Yup.string(),
  color: Yup.string().required("Por favor seleccione un color"),
  shortName: Yup.string()
    .matches(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      "El nombre incluye caracteres no válidos"
    )
    .required("El nombre corto es requerido"),
  contactName: Yup.string().required("El nombre de contacto es requerido"),
  contactEmail: Yup.string()
    .email("El email debe ser válido")
    .required("El email de contacto es requerido"),
  contactPhone: Yup.string()
    .matches(
      //eslint-disable-next-line
      /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]{9,}$/g,
      "Debe ser un teléfono válido"
    )
    .required("El teléfono de contacto es requerido"),
});

const EditStorePage = ({ history, match }) => {
  const id = match.params.slug;
  const inputLogoRef = useRef(null);
  const inputBannerRef = useRef(null);
  const [logo, setLogo] = useState("");
  const [banner, setBanner] = useState("");
  const [rawLogo, setRawLogo] = useState(null);
  const [rawBanner, setRawBanner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    name: "",
    description: "",
    logo: "",
    banner: "",
    color: "",
    shortName: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
  });
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  useEffect(() => {
    if (!userInfo) return <Redirect to="/login" />;
    if (!id) return <Redirect to="/admin/stores" />;
  }, [id, userInfo]);

  useEffect(() => {
    if (!userInfo || !id) return;
    getStore();
    // eslint-disable-next-line
  }, [id, userInfo]);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogo(e.target.result);
        setRawLogo(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setBanner(e.target.result);
        setRawBanner(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const getStore = async () => {
    try {
      const { data } = await API.get(`/stores?slug=${id}`);
      const store = data?.store[0] || false;

      if (!data.ok || !store) return history.replace(`/admin/stores`);

      setInitialValues({
        id: store._id,
        name: store.name,
        description: store.description,
        logo: store.logo,
        banner: store.banner,
        color: store.color,
        shortName: store.shortName,
        contactName: store.contactInfo.name,
        contactEmail: store.contactInfo.email,
        contactPhone: store.contactInfo.phone,
        visible: store.visible,
      });
      setBanner(store.banner);
      setLogo(store.logo);
    } catch (error) {
      return history.replace("/admin/stores");
    }
  };

  const uploadImagesToS3 = async ({ logo, banner }) => {
    if (!rawLogo && !rawBanner) return { logo: false, banner: false };
    const formDataLogo = new FormData();
    const formDataBanner = new FormData();
    formDataLogo.append("file", logo);
    formDataBanner.append("file", banner);

    try {

      let logoRes = { data: { location: { url: initialValues.logo } } };
      let bannerRes = { data: { location: { url: initialValues.banner } } };

      if(rawLogo){
        logoRes = await API.post("/uploads", formDataLogo, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        setLogo(logoRes.data.location.url);
      }

      if(rawBanner){
        bannerRes = await API.post("/uploads", formDataBanner, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        setBanner(bannerRes.data.location.url);
      }

      return {
        logo: logoRes.data,
        banner: bannerRes.data,
      };
    } catch (err) {
      throw new Error({ message: "Error al subir las imágenes" });
    }
  };

  const onSubmit = async (values, actions) => {
    setLoading(true);
    let tempKeys = [];
    try {
      const { logo, banner } = await uploadImagesToS3({
        logo: rawLogo,
        banner: rawBanner,
      });

      const urls = {
        logo: logo ? logo.location?.url : initialValues.logo,
        banner: banner ? banner.location?.url : initialValues.banner,
      };

      if (logo) tempKeys.push(logo.location.public_id);
      if (banner) tempKeys.push(banner.location.public_id);

      const data = {
        ...values,
        ...urls,
        contactInfo: {
          name: values.contactName,
          email: values.contactEmail,
          phone: values.contactPhone,
        },
        visible: values.visible,
      };

      const id = initialValues.id;
      await API.put(`/stores/${id}`, data);
      toast.success("Cambios guardados con éxito");
      actions.resetForm();
      setLoading(false);
      history.replace("/admin/stores");
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      toast.error(message || "Algo salió mal, intente nuevamente");
      setLoading(false);
      await deleteImagesFromS3(tempKeys);
    }
  };

  const deleteImagesFromS3 = (keys) => {
    return Promise.all(
      keys.map(async (key) => {
        await API.delete(`/uploads`, { data: { id: key } });
      })
    );
  };

  return (
    <>
      <Helmet>
        <title>Editar Tienda - Skyshops</title>
      </Helmet>
      <Flex
        flex={1}
        minH="300px"
        h="100%"
        mt="70px"
        py="40px"
        px={{ base: 0, md: "20px" }}
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <Text fontSize="1.5em" fontWeight="400">
          ¡Crea tu tienda!
        </Text>
        <Flex
          mt="40px"
          w="100%"
          pb="120px"
          pt="80px"
          px={{ base: "40px", md: "40px" }}
          flex={1}
          minH="100%"
          bg="gray.100"
          borderRadius="10px"
          flexDir={"column"}
        >
          <Formik
            onSubmit={(values, actions) => onSubmit(values, actions)}
            validationSchema={validationSchema}
            initialValues={initialValues}
            enableReinitialize
          >
            {({
              values,
              handleChange,
              handleBlur,
              errors,
              dirty,
              isValid,
              isSubmitting,
              setFieldValue,
              handleSubmit,
            }) => (
              <Form
                onSubmit={handleSubmit}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Flex flexDir="column" mb="90px" w="100%">
                  <SectionHeader
                    title="Elige un nombre"
                    description="Este es el nombre que los usuarios verán al visitarte, podrás editarlo más adelante."
                  />

                  <Flex w="100%" flexWrap={"wrap"}>
                    <Input
                      name="name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.name}
                      placeholder="Café Real"
                      style={{
                        width: "100%",
                        backgroundColor: "white",
                      }}
                    />
                  </Flex>

                  <FormError error={errors.name} />
                </Flex>

                <Flex flexDir="column" mb="90px" w="100%">
                  <SectionHeader
                    title="Cuéntanos sobre tu negocio"
                    description="Deja que la gente conozca más sobre tu negocio y los productos que ofreces."
                  />

                  <Flex w="100%" flexWrap={"wrap"}>
                    <Textarea
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.description}
                      name="description"
                      placeholder="Hola, somos una empresa dedicada a la venta de productos del mundo del café. Nuestro objetivo es brindar a nuestros clientes productos de calidad."
                      type="textarea"
                      noOfLines={10}
                      style={{
                        width: "100%",
                        backgroundColor: "white",
                      }}
                    />
                  </Flex>

                  <FormError error={errors.description} />
                </Flex>

                <Flex flexDir="column" mb="90px" w="100%">
                  <SectionHeader
                    title="Tu Logo"
                    description="Sube tu logo para que tus clientes puedan reconocerte facilmente. Si no tienes uno, mostraremos el nombre de tu negocio."
                  />

                  <Input
                    onChange={handleLogoChange}
                    ref={inputLogoRef}
                    name="logo"
                    type="file"
                    display="none"
                  />

                  <Flex
                    w="100%"
                    flexWrap={"wrap"}
                    justifyContent="center"
                    alignItems={"center"}
                    flexDir="column"
                  >
                    <Flex
                      pos={"relative"}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Img
                        src={logo || ""}
                        mb="10px"
                        h="200px"
                        w="200px"
                        bg="white"
                        objectFit={"contain"}
                        boxShadow="0px 0px 10px rgba(0,0,0,0.1)"
                        borderRadius="10px"
                      />
                      {!logo && (
                        <Text alignSelf={"center"} pos="absolute" zIndex={1}>
                          {values.name || "Tu Logo"}
                        </Text>
                      )}
                    </Flex>
                    <Button
                      onClick={() => inputLogoRef.current.click()}
                      w="160px"
                      justifyContent="space-between"
                    >
                      Subir archivo <MdFileUpload />
                    </Button>
                    <FormError error={errors.logo} />
                  </Flex>
                </Flex>

                <Flex flexDir="column" mb="90px" w="100%">
                  <SectionHeader
                    title="Tu Portada"
                    description="Sube una imagen interesante, que represente tu negocio y que tus clientes puedan reconocer. Si no tienes una, mostraremos el color que eligas."
                  />
                  <Input
                    onChange={handleBannerChange}
                    ref={inputBannerRef}
                    name="banner"
                    type="file"
                    display="none"
                  />

                  <Flex
                    w="100%"
                    flexWrap={"wrap"}
                    justifyContent="center"
                    alignItems={"center"}
                    flexDir="column"
                  >
                    <Flex
                      pos={"relative"}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Img
                        src={banner || ""}
                        mb="10px"
                        objectFit="cover"
                        h="200px"
                        w="500px"
                        maxW={"100%"}
                        bg={values.color || "white"}
                        boxShadow="0px 0px 10px rgba(0,0,0,0.1)"
                        borderRadius="10px"
                      />
                      {!banner && (
                        <Text alignSelf={"center"} pos="absolute" zIndex={1}>
                          {values.name || "Tu Portada"}
                        </Text>
                      )}
                    </Flex>
                    <Button
                      onClick={() => inputBannerRef.current.click()}
                      w="160px"
                      justifyContent="space-between"
                    >
                      Subir archivo <MdFileUpload />
                    </Button>
                    <FormError error={errors.banner} />
                  </Flex>
                </Flex>

                <Flex flexDir="column" mb="90px" w="100%">
                  <Input
                    type="hidden"
                    name="color"
                    value={values.color}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <SectionHeader
                    title="Elige un color"
                    description="Este será el color principal de tu tienda, te sugerimos elegir uno que combine con tu logo."
                  />

                  <ColorPicker
                    colors={colors}
                    selectColor={(color) => setFieldValue("color", color)}
                    value={values.color}
                  />

                  <FormError error={errors.color} />
                </Flex>

                <Flex flexDir="column" mb="90px" w="100%">
                  <SectionHeader
                    title="Elige un nombre corto"
                    description="Este es el nombre que los usuarios verán en la barra de direcciones de su navegador, te sugerimos no cambiarlo más adelante ya que tus clientes podría perderte de vista."
                  />
                  {values.name && (
                    <Flex>
                      <Text color="gray.500" fontSize=".8em">
                        Sugerencia: {slugify(values.name.toLowerCase())}
                      </Text>
                      <Button
                        ml="5px"
                        size="xs"
                        variant={"link"}
                        onClick={() =>
                          setFieldValue(
                            "shortName",
                            slugify(values.name.toLowerCase())
                          )
                        }
                        p="0"
                      >
                        Click para usar
                      </Button>
                    </Flex>
                  )}

                  <Flex w="100%" flexWrap={"wrap"}>
                    <InputGroup background={"white"}>
                      <InputLeftAddon
                        background={"gray.200"}
                        children={
                          <>
                            <Text display={{ base: "none", md: "flex" }}>
                              https://www.skyshops.com/
                            </Text>
                            <Flex display={{ base: "flex", md: "none" }}>
                              <IoLink />
                            </Flex>
                          </>
                        }
                        fontWeight={"300"}
                      />
                      <Input
                        type="text"
                        placeholder={
                          values.name
                            ? slugify(values.name.toLowerCase())
                            : "cafereal"
                        }
                        name="shortName"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.shortName}
                      />
                    </InputGroup>
                    <Text display={{ base: "flex", md: "none" }} mt="10px">
                      https://www.skyshops.com/mi_tienda
                    </Text>
                  </Flex>
                  <FormError error={errors.shortName} />
                </Flex>

                <Flex flexDir="column" mb="90px" w="100%">
                  <SectionHeader
                    title="Finalimente, cuéntanos sobre ti"
                    description="Estos datos servirán para que tus clientes puedan comunicarse contigo y para darte un mejor servicio."
                  />

                  <Flex w="100%" flexWrap={"wrap"}>
                    <Flex flexDir="column" w="100%" mb="10px">
                      <Text fontSize=".8em">Nombre Completo</Text>
                      <InputGroup background={"white"}>
                        <InputLeftAddon
                          background={"gray.200"}
                          children={<FiUser />}
                        />
                        <Input
                          type="text"
                          placeholder="John Doe"
                          name="contactName"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.contactName}
                        />
                      </InputGroup>
                      <FormError error={errors.contactName} />
                    </Flex>

                    <Flex flexDir="column" w="100%" mb="10px">
                      <Text fontSize=".8em">Email</Text>
                      <InputGroup background={"white"}>
                        <InputLeftAddon
                          background={"gray.200"}
                          children={<MdEmail />}
                        />
                        <Input
                          type="text"
                          placeholder="tienda@asombrosa.com"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="contactEmail"
                          value={values.contactEmail}
                        />
                      </InputGroup>
                      <FormError error={errors.contactEmail} />
                    </Flex>

                    <Flex flexDir="column" w="100%">
                      <Text fontSize=".8em">Teléfono</Text>
                      <InputGroup background={"white"}>
                        <InputLeftAddon
                          background={"gray.200"}
                          children={<MdPhoneIphone />}
                        />
                        <Input
                          type="tel"
                          placeholder="+52 5555555555"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="contactPhone"
                          value={values.contactPhone}
                        />
                      </InputGroup>
                      <FormError error={errors.contactPhone} />
                    </Flex>
                  </Flex>
                </Flex>

                <Flex flexDir="column" mb="90px" w="100%">
                  <SectionHeader
                    title="Visibilidad"
                    description={`Elige si quieres que tu tienda se encuentre visible en: https://skyshops.com/${values.shortName}`}
                  />

                  <Flex
                    w="100%"
                    flexWrap={"wrap"}
                  >
                    <Switch
                    pt='2px'
                      mr="10px"
                      id="visible"
                      name="visible"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.visible}
                      isChecked={values.visible}
                    />
                    <Text mb='40px'>{values.visible ? "Visible" : "No visible"}</Text>
                  </Flex>
                  <FormError error={errors.visible} />
                </Flex>

                <Flex flexDir="column" w="100%">
                  <SectionHeader
                    title="¡Ya estás listo!"
                    description="Puedes crear tu tienda ahora o echar un vistazo, también puedes ocultar tu tienda al público en cualquier momento. Al crear una tienda en Skyshops, aceptas nuestros terminos y condiciones."
                  />
                  <Flex
                    w="100%"
                    flexWrap={"wrap"}
                    alignItems={"start"}
                    justifyContent="flex-start"
                  >
                    <Button
                      type="submit"
                      disabled={
                        loading || isSubmitting || !(isValid && dirty) 
                        && !(rawLogo || rawBanner)
                      }
                      _disabled={{ opacity: 0.4 }}
                      w="100%"
                      justifyContent="space-between"
                      colorScheme="blue"
                      maxW={"130px"}
                      minW={"130px"}
                      isLoading={isSubmitting || loading}
                      loadingText="Guardando..."
                    >
                      Guardar <MdStore />
                    </Button>
                    {/* 
                    <Button
                      disabled={loading || isSubmitting || !(isValid && dirty)}
                      _disabled={{ opacity: 0.4 }}
                      ml={{ base: 0, md: "10px" }}
                      mt={{ base: "10px", md: 0 }}
                      w="100%"
                      justifyContent="space-between"
                      bg="gray.200"
                      maxW={"160px"}
                    >
                      Vista Previa <IoMdEye />
                    </Button> */}
                  </Flex>
                </Flex>
              </Form>
            )}
          </Formik>
        </Flex>
      </Flex>
    </>
  );
};

export default withNavElements(EditStorePage);